import { c as _c } from "react/compiler-runtime";
import { AnimateLayoutChanges, defaultAnimateLayoutChanges, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from '@headlessui/react';
import { Tooltip } from '@systemeio/ui';
import { nonDraggableProps } from 'modules/courses/courses/curriculum/utils/non-draggable-props';
import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { twMerge } from 'tailwind-merge';
import { ModuleId, QuizInterface } from '../types/module-interface';
import { ChevronDownIcon, QuizStatusIcon } from '@systemeio/ui/icons';
export type CourseModuleProps = PropsWithChildren & {
  childrenCount: number;
  openDefault: boolean;
  module: {
    id: ModuleId;
    name: string;
    lectures: unknown[];
    quiz?: QuizInterface | null;
  };
  moduleActions: React.ReactNode;
  onSelect?: (CurriculumId: number) => void;
};
const animateLayoutChanges: AnimateLayoutChanges = args => defaultAnimateLayoutChanges({
  ...args,
  wasDragging: true
});
export function CourseModule(t0) {
  const $ = _c(62);
  const {
    module,
    children,
    childrenCount,
    openDefault,
    moduleActions,
    onSelect
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const shouldBeAlwaysOpened = module.lectures.length === 0;
  const [open, setOpen] = useState(shouldBeAlwaysOpened);
  let t1;
  let t2;
  if ($[0] !== shouldBeAlwaysOpened) {
    t1 = () => {
      if (shouldBeAlwaysOpened) {
        setOpen(shouldBeAlwaysOpened);
      }
    };
    t2 = [shouldBeAlwaysOpened];
    $[0] = shouldBeAlwaysOpened;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== module.lectures) {
    t3 = {
      type: "container",
      children: module.lectures
    };
    $[3] = module.lectures;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== module.id || $[6] !== t3) {
    t4 = {
      id: module.id,
      data: t3,
      animateLayoutChanges
    };
    $[5] = module.id;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transition,
    transform
  } = useSortable(t4);
  let t5;
  if ($[8] !== transform) {
    t5 = CSS.Translate.toString(transform);
    $[8] = transform;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = isDragging ? 0.5 : undefined;
  let t7;
  if ($[10] !== t5 || $[11] !== t6 || $[12] !== transition) {
    t7 = {
      transition,
      transform: t5,
      opacity: t6
    };
    $[10] = t5;
    $[11] = t6;
    $[12] = transition;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const style = t7;
  let t8;
  if ($[14] !== style) {
    t8 = {
      ...style
    };
    $[14] = style;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = `course-module-${module.id}-div`;
  const t10 = openDefault || shouldBeAlwaysOpened;
  const t11 = `bg-brand-primary-subtle flex items-center ${childrenCount > 0 ? "" : "overflow-hidden rounded-b-lg"}`;
  let t12;
  if ($[16] !== module.id || $[17] !== onSelect) {
    t12 = () => onSelect?.(module.id);
    $[16] = module.id;
    $[17] = onSelect;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== open || $[20] !== shouldBeAlwaysOpened) {
    t13 = () => setOpen(shouldBeAlwaysOpened || !open);
    $[19] = open;
    $[20] = shouldBeAlwaysOpened;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const t14 = `h-3 w-3 ${open ? "rotate-180 transform" : ""} shrink-0`;
  let t15;
  if ($[22] !== t14) {
    t15 = <ChevronDownIcon className={t14} aria-hidden="true" />;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== module.name) {
    t16 = <span className="truncate" data-test="course-module-name">{module.name}</span>;
    $[24] = module.name;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t13 || $[27] !== t15 || $[28] !== t16) {
    t17 = <DisclosureButton onClick={t13} className="text-text-primary font-inter text-start relative flex h-full w-full max-w-fit cursor-pointer items-center gap-2 px-4 py-4 text-base font-bold lg:px-6 lg:text-[17px]" {...nonDraggableProps} data-test="course-module-button-show-lecture">{t15}{t16}</DisclosureButton>;
    $[26] = t13;
    $[27] = t15;
    $[28] = t16;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t12 || $[31] !== t17) {
    t18 = <div onClick={t12} className="order-1 flex-[25_1_0%] truncate lg:flex-[55_1_0%]">{t17}</div>;
    $[30] = t12;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="order-2 hidden flex-[15_1_0%] px-6 py-4 lg:block"><div /></div>;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== module.quiz || $[35] !== t) {
    t20 = module.quiz && <div {...nonDraggableProps}><Tooltip label={module.quiz.published ? t("dashboard.course.module_quiz_active") : t("dashboard.course.module_quiz_inactive")}><QuizStatusIcon className={twMerge("h-[20px] w-[20px]", module.quiz.published ? "fill-brand-primary" : "fill-fill-primary")} /></Tooltip></div>;
    $[34] = module.quiz;
    $[35] = t;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== moduleActions || $[38] !== t20) {
    t21 = <div className="order-2 flex flex-[5_1_0%] items-center justify-end gap-6 px-4 py-4 lg:order-3 lg:px-6">{t20}{moduleActions}</div>;
    $[37] = moduleActions;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t11 || $[41] !== t18 || $[42] !== t21) {
    t22 = <div className={t11}>{t18}{t19}{t21}</div>;
    $[40] = t11;
    $[41] = t18;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== children) {
    t23 = <DisclosurePanel static={true} className="bg-background-secondary">{children}</DisclosurePanel>;
    $[44] = children;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== open || $[47] !== t23) {
    t24 = <Transition show={open} enter="transition ease duration-500 transform" enterFrom="opacity-0 translate-y-0" enterTo="opacity-100 translate-y-0" leave="transition ease duration-300 transform" leaveFrom="opacity-100 translate-y-0" leaveTo="opacity-0 translate-y-0" data-test="course-module-expanded-attribute">{t23}</Transition>;
    $[46] = open;
    $[47] = t23;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== t22 || $[50] !== t24) {
    t25 = <>{t22}{t24}</>;
    $[49] = t22;
    $[50] = t24;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== t10 || $[53] !== t25) {
    t26 = <div className="z-0 overflow-hidden rounded-lg"><Disclosure defaultOpen={t10}>{t25}</Disclosure></div>;
    $[52] = t10;
    $[53] = t25;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== attributes || $[56] !== listeners || $[57] !== setNodeRef || $[58] !== t26 || $[59] !== t8 || $[60] !== t9) {
    t27 = <div className="flex cursor-move flex-col gap-3 focus-visible:outline-hidden" ref={setNodeRef} style={t8} {...attributes} {...listeners} data-test={t9}>{t26}</div>;
    $[55] = attributes;
    $[56] = listeners;
    $[57] = setNodeRef;
    $[58] = t26;
    $[59] = t8;
    $[60] = t9;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  return t27;
}